import React, { useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { SelectChangeEvent } from '@mui/material';
import { useTranslation } from 'react-i18next';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import ValidationResult from '@common/interfaces/api/validation-result';
import { DownArrow, MoveRightArrow, SearchIcon } from '@common/utils/icons/icons';
import { sortByDate } from '@common/utils/date';
import { useAppSelector } from '../../store';
import { selectTorizonPackages } from '../../store/general/general-slice-selectors';
import TorizonPackage from '../../models/general/versions/torizon-package';

interface TorizonPackageGroup {
  name: string;
  versions: TorizonPackage[];
}

const groupPackagesByName = (packages: TorizonPackage[]): TorizonPackageGroup[] => {
  const byName = new Map<string, TorizonPackage[]>();
  packages.forEach((p) => {
    const group = byName.get(p.name);
    if (group != null) {
      group.push(p);
    } else {
      byName.set(p.name, [p]);
    }
  });

  return Array.from(byName.entries())
    .map(([name, versions]) => ({
      name,
      // Newest first, since we most likely want to use the newest
      versions: versions.sort((a, b) => sortByDate(a.createdAt, b.createdAt)),
    }))
    .sort((a, b) => sortByDate(a.versions[0].createdAt, b.versions[0].createdAt));
};

const TorizonVersionsForm: React.FC<{
  label: string;
  initialValueId?: string | null;
  value: string;
  onChange: (event: SelectChangeEvent<unknown>, child: React.ReactNode) => void;
  validationResult: ValidationResult;
  packageType: string;
}> = ({ label, value, initialValueId, onChange, validationResult, packageType }) => {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');
  const [expandedName, setExpandedName] = useState<string | null>(null);
  const fieldRef = useRef<HTMLDivElement>(null);

  const torizonPackages = useAppSelector((state) => selectTorizonPackages(state));

  const getLabelById = (id?: string | null) => {
    if (id == null || id.length === 0) return t('info-not-set');
    const entry = torizonPackages.data?.find((p) => p.id === id);
    if (entry == null) return t('info-torizon-package-deleted');
    return `${entry.name} · ${entry.version}`;
  };

  const groups = useMemo(() => {
    const packages = (torizonPackages.data ?? []).filter((p) => p.type === packageType);
    return groupPackagesByName(packages);
  }, [torizonPackages.data, packageType]);

  const visibleGroups = useMemo(() => {
    const cleanedSearch = search.trim().toLowerCase();
    if (cleanedSearch === '') return groups;
    return groups.filter((group) => group.name.toLowerCase().includes(cleanedSearch));
  }, [groups, search]);

  const isLoading = torizonPackages.rest.getStatus === HttpFetchStatus.Loading;
  const hasFailed = torizonPackages.rest.getStatus === HttpFetchStatus.Failed;

  // Anchored to the field itself (not the clicked element) so the dropdown opens at the same
  // spot and width whether you click the field or, in edit mode, the current-value display.
  const openHandler = () => {
    if (isLoading || hasFailed) return;
    setAnchorEl(fieldRef.current);
    setExpandedName(torizonPackages.data?.find((p) => p.id === value)?.name ?? null);
  };

  // The trigger is a readOnly TextField, so it needs an explicit keyboard path to open --
  // and this also stops Enter from falling through to the enclosing form's submit.
  const openKeyDownHandler = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    openHandler();
  };

  const closeHandler = () => {
    setAnchorEl(null);
    setSearch('');
    setExpandedName(null);
  };

  // Popper has no built-in modal behavior (that's the point -- see the note below), so it
  // doesn't close on Escape for free the way the Popover it replaced did.
  const closeOnEscapeHandler = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') closeHandler();
  };

  const nameClickHandler = (name: string) => () => {
    setExpandedName((current) => (current === name ? null : name));
  };

  const versionClickHandler = (pkg: TorizonPackage) => () => {
    onChange({ target: { value: pkg.id } } as SelectChangeEvent<unknown>, null);
    closeHandler();
  };

  return (
    <Grid
      container
      onClick={openHandler}
      sx={{
        backgroundColor:
          value !== initialValueId && initialValueId != null ? 'rgb(237, 247, 237)' : undefined,
        p: 1,
        borderRadius: 1,
        cursor: isLoading || hasFailed ? undefined : 'pointer',
      }}
    >
      {initialValueId != null && (
        <React.Fragment>
          <Grid item xs={12}>
            <InputLabel shrink>{label}</InputLabel>
          </Grid>
          <Grid item xs={3} alignContent="center">
            <Typography>{getLabelById(initialValueId)}</Typography>
          </Grid>
          <Grid item xs={1} alignContent="center">
            <MoveRightArrow />
          </Grid>
        </React.Fragment>
      )}
      <Grid item xs={initialValueId != null ? 8 : 12} ref={fieldRef}>
        <TextField
          fullWidth
          label={initialValueId == null ? label : undefined}
          value={getLabelById(value)}
          onKeyDown={openKeyDownHandler}
          error={hasFailed || (validationResult !== undefined && !validationResult.isValid)}
          helperText={
            hasFailed
              ? t('error-torizon-catalogue-unavailable')
              : isLoading
                ? t('loading-versions')
                : validationResult !== undefined && validationResult.error !== null
                  ? t(validationResult.error)
                  : undefined
          }
          InputProps={{
            readOnly: true,
            endAdornment: isLoading ? <CircularProgress size={18} /> : undefined,
          }}
        />
        <Popper
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="bottom-start"
          sx={(theme) => ({ zIndex: theme.zIndex.modal + 1 })}
        >
          <ClickAwayListener onClickAway={closeHandler}>
            <Paper
              onKeyDown={closeOnEscapeHandler}
              // Popper content is a portal, but React still bubbles clicks through the
              // component tree -- without this, selecting a version reopens the field's
              // own onClick up the chain.
              onClick={(event: React.MouseEvent) => event.stopPropagation()}
              style={{ width: anchorEl?.clientWidth }}
            >
              <Box sx={{ p: 1 }}>
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  value={search}
                  placeholder={t('input-label-search')}
                  inputProps={{ 'aria-label': t('input-label-search') }}
                  onChange={(event) => setSearch(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <List sx={{ maxHeight: 360, overflowY: 'auto' }}>
                {visibleGroups.map((group) => (
                  <React.Fragment key={group.name}>
                    <ListItemButton onClick={nameClickHandler(group.name)}>
                      <Stack direction="row" justifyContent="space-between" width="100%">
                        <Typography>{group.name}</Typography>
                        <DownArrow
                          sx={{
                            transform: expandedName === group.name ? 'rotate(180deg)' : undefined,
                            transition: 'transform 0.4s ease-in-out',
                          }}
                        />
                      </Stack>
                    </ListItemButton>
                    <Collapse in={expandedName === group.name} timeout="auto" unmountOnExit>
                      <List disablePadding>
                        {group.versions.map((p) => (
                          <ListItemButton
                            key={p.id}
                            selected={p.id === value}
                            onClick={versionClickHandler(p)}
                            sx={{ pl: 4 }}
                          >
                            <Stack direction="row" justifyContent="space-between" width="100%">
                              <Typography variant="body2">{p.version}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {p.createdAt.slice(0, 10)}
                              </Typography>
                            </Stack>
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                  </React.Fragment>
                ))}
                {visibleGroups.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
                    {t('fleet-management-filter-no-matches')}
                  </Typography>
                )}
              </List>
            </Paper>
          </ClickAwayListener>
        </Popper>
      </Grid>
    </Grid>
  );
};

export default TorizonVersionsForm;
